import type { RunSearch, RunsSearch } from "./views/repo/Runs";
import {
  createBrowserHistory,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  parseSearchWith,
  stringifySearchWith,
} from "@tanstack/react-router";
import { AppShell } from "./shell/AppShell";
import type { WelcomeSearch } from "./views/Welcome";
import { ErrorState, NotFound } from "./ui";

// Each screen is its own chunk, loaded on first visit (and preloaded on hover).
const RefView = lazyRouteComponent(() => import("./views/repo/Ref"), "RefView");
const RepoLayout = lazyRouteComponent(() => import("./views/repo/RepoLayout"), "RepoLayout");
const HomeView = lazyRouteComponent(() => import("./views/Home"), "HomeView");
const InboxView = lazyRouteComponent(() => import("./views/Inbox"), "InboxView");
const StatusView = lazyRouteComponent(() => import("./views/Status"), "StatusView");
const ReviewQueueView = lazyRouteComponent(() => import("./views/ReviewQueue"), "ReviewQueueView");
const ExploreView = lazyRouteComponent(() => import("./views/Explore"), "ExploreView");
const OrgView = lazyRouteComponent(() => import("./views/Org"), "OrgView");
const TokensView = lazyRouteComponent(() => import("./views/Tokens"), "TokensView");
const AgentsView = lazyRouteComponent(() => import("./views/repo/Agents"), "AgentsView");
const ReleasesView = lazyRouteComponent(() => import("./views/repo/Releases"), "ReleasesView");
const ReleaseView = lazyRouteComponent(() => import("./views/repo/Releases"), "ReleaseView");
const RunsView = lazyRouteComponent(() => import("./views/repo/Runs"), "RunsView");
const RunView = lazyRouteComponent(() => import("./views/repo/Runs"), "RunView");
const SearchView = lazyRouteComponent(() => import("./views/repo/Search"), "SearchView");
const ForksView = lazyRouteComponent(() => import("./views/repo/Forks"), "ForksView");
const MilestonesView = lazyRouteComponent(() => import("./views/repo/Plan"), "MilestonesView");
const LabelsView = lazyRouteComponent(() => import("./views/repo/Plan"), "LabelsView");
const ChangesView = lazyRouteComponent(() => import("./views/Changes"), "ChangesView");
const CodeView = lazyRouteComponent(() => import("./views/Code"), "CodeView");
const LocksView = lazyRouteComponent(() => import("./views/Locks"), "LocksView");
const OverviewView = lazyRouteComponent(() => import("./views/repo/Overview"), "OverviewView");
const ReviewView = lazyRouteComponent(() => import("./views/repo/Review"), "ReviewView");
const ReviewsView = lazyRouteComponent(() => import("./views/repo/Reviews"), "ReviewsView");
const IssuesView = lazyRouteComponent(() => import("./views/repo/Issues"), "IssuesView");
const IssueView = lazyRouteComponent(() => import("./views/repo/Issue"), "IssueView");
const RepoSettingsView = lazyRouteComponent(() => import("./views/repo/Settings"), "RepoSettingsView");
const WelcomeView = lazyRouteComponent(() => import("./views/Welcome"), "WelcomeView");

const str = (v: unknown) => (typeof v === "string" && v ? v : typeof v === "number" ? String(v) : undefined);
const flag = (v: unknown) => v === true || v === "true" || v === 1 || undefined;

const root = createRootRoute({ component: AppShell });

const home = createRoute({ getParentRoute: () => root, path: "/", component: HomeView });
const inbox = createRoute({ getParentRoute: () => root, path: "/inbox", component: InboxView });
const statusRoute = createRoute({ getParentRoute: () => root, path: "/status", component: StatusView });
const reviewsQueue = createRoute({ getParentRoute: () => root, path: "/reviews", component: ReviewQueueView });
export type ExploreSearch = { q?: string; topic?: string; sort?: "busiest" | "stars" | "landed" | "newest"; by?: "agents" | "people" };
const explore = createRoute({
  getParentRoute: () => root,
  path: "/explore",
  validateSearch: (s): ExploreSearch => ({
    q: str(s.q),
    topic: str(s.topic)?.toLowerCase(),
    sort: (["busiest", "stars", "landed", "newest"] as const).find((v) => v === s.sort),
    by: (["agents", "people"] as const).find((v) => v === s.by),
  }),
  component: ExploreView,
});
const welcome = createRoute({
  getParentRoute: () => root,
  path: "/welcome",
  component: WelcomeView,
  validateSearch: (s): WelcomeSearch => ({
    step: (["plan", "org", "pay", "invite", "repo", "agent"] as const).find((v) => v === s.step),
    org: typeof s.org === "string" && /^[A-Za-z0-9._-]{1,100}$/.test(s.org) ? s.org : undefined,
    plan: s.plan === "team" ? "team" : s.plan === "free" ? "free" : undefined,
  }),
});
const ConnectView = lazyRouteComponent(() => import("./views/Connect"), "ConnectView");
const connectRoute = createRoute({ getParentRoute: () => root, path: "/connect", component: ConnectView });
const ImportView = lazyRouteComponent(() => import("./views/Import"), "ImportView");
const ImportProgressView = lazyRouteComponent(() => import("./views/Import"), "ImportProgressView");
const importRoute = createRoute({
  getParentRoute: () => root,
  path: "/import",
  component: ImportView,
  validateSearch: (s): { from?: string; owner?: string } => ({
    from: typeof s.from === "string" && s.from.startsWith("https://") && s.from.length < 300 ? s.from : undefined,
    owner: typeof s.owner === "string" && /^[A-Za-z0-9._-]{1,100}$/.test(s.owner) ? s.owner : undefined,
  }),
});
const importProgress = createRoute({ getParentRoute: () => root, path: "/import/$id", component: ImportProgressView });
const NewRepositoryView = lazyRouteComponent(() => import("./views/NewRepositoryPage"), "NewRepositoryPage");
const newRepo = createRoute({
  getParentRoute: () => root,
  path: "/new",
  component: NewRepositoryView,
  validateSearch: (s): { owner?: string } => ({ owner: typeof s.owner === "string" && /^[A-Za-z0-9._-]{1,100}$/.test(s.owner) ? s.owner : undefined }),
});
const tokens = createRoute({ getParentRoute: () => root, path: "/settings/tokens", component: TokensView });
const NotificationsView = lazyRouteComponent(() => import("./views/Notifications"), "NotificationsView");
const notifications = createRoute({ getParentRoute: () => root, path: "/settings/notifications", component: NotificationsView });
const RunnersView = lazyRouteComponent(() => import("./views/Runners"), "RunnersView");
const runnersRoute = createRoute({ getParentRoute: () => root, path: "/settings/runners", component: RunnersView });

export type OrgSearch = { tab?: "repositories" | "people" | "agents" | "teams" | "runners" | "secrets" | "usage"; agent?: string };
const org = createRoute({
  getParentRoute: () => root,
  path: "/$owner",
  validateSearch: (s): OrgSearch => ({
    tab: (["repositories", "people", "agents", "teams", "runners", "secrets", "usage"] as const).find((t) => t === s.tab),
    agent: str(s.agent),
  }),
  component: OrgView,
});

/** `ref` is shared by every view of a repository: the branch being looked at. */
export type RefSearch = { ref?: string };
const repo = createRoute({
  getParentRoute: () => root,
  path: "/$owner/$repo",
  validateSearch: (s): RefSearch => ({ ref: str(s.ref) }),
  component: RepoLayout,
});

const overview = createRoute({ getParentRoute: () => repo, path: "/", component: OverviewView });

export type ChangesSearch = { c?: string };
const changes = createRoute({
  getParentRoute: () => repo,
  path: "/changes",
  validateSearch: (s): ChangesSearch => ({ c: str(s.c) }),
  component: ChangesView,
});

export type CodeSearch = { at?: string; blame?: boolean; history?: boolean; L?: string };
const code = createRoute({
  getParentRoute: () => repo,
  path: "/code/$",
  validateSearch: (s): CodeSearch => ({ at: str(s.at), blame: flag(s.blame), history: flag(s.history), L: str(s.L) }),
  component: CodeView,
});

export type ReviewsSearch = {
  q?: string;
  view?: "open" | "ready" | "blocked" | "agents" | "mine" | "drafts" | "landed" | "closed";
  sort?: "oldest" | "updated" | "largest";
};
const reviews = createRoute({
  getParentRoute: () => repo,
  path: "/reviews",
  validateSearch: (s): ReviewsSearch => ({
    q: str(s.q),
    view: (["open", "ready", "blocked", "agents", "mine", "drafts", "landed", "closed"] as const).find((v) => v === s.view),
    sort: (["oldest", "updated", "largest"] as const).find((v) => v === s.sort),
  }),
  component: ReviewsView,
});

export type ReviewSearch = { file?: string; layout?: "unified" | "split"; tab?: "files"; comment?: string };
const review = createRoute({
  getParentRoute: () => repo,
  path: "/reviews/$number",
  validateSearch: (s): ReviewSearch => ({
    file: str(s.file),
    layout: s.layout === "split" ? "split" : undefined,
    tab: s.tab === "files" ? "files" : undefined,
    comment: str(s.comment),
  }),
  component: ReviewView,
});

export type IssueView = "open" | "mine" | "unassigned" | "fixing" | "authored" | "closed";
export type IssuesSearch = { q?: string; view?: Exclude<IssueView, "open">; sort?: "oldest" | "updated" | "comments" };
const issues = createRoute({
  getParentRoute: () => repo,
  path: "/issues",
  validateSearch: (s): IssuesSearch => ({
    q: str(s.q),
    // Older links said ?closed=true.
    view: flag(s.closed) ? "closed" : (["mine", "unassigned", "fixing", "authored", "closed"] as const).find((v) => v === s.view),
    sort: (["oldest", "updated", "comments"] as const).find((v) => v === s.sort),
  }),
  component: IssuesView,
});
/** `comment`: a comment to scroll to and point out (from a mention in the inbox or a mail). */
export type CommentSearch = { comment?: string };
const issue = createRoute({
  getParentRoute: () => repo,
  path: "/issues/$number",
  validateSearch: (s): CommentSearch => ({ comment: str(s.comment) }),
  component: IssueView,
});
// #12 in text: an issue and a change request never share a number, so one link finds either.
const ref = createRoute({ getParentRoute: () => repo, path: "/ref/$number", validateSearch: (s): CommentSearch => ({ comment: str(s.comment) }), component: RefView });
/** `new` opens the new-release form, with a tag to release if it names one. */
export type ReleasesSearch = { new?: string };
const releases = createRoute({
  getParentRoute: () => repo,
  path: "/releases",
  validateSearch: (s): ReleasesSearch => ({ new: s.new === undefined ? undefined : String(s.new === true ? "" : s.new) }),
  component: ReleasesView,
});
const release = createRoute({ getParentRoute: () => repo, path: "/releases/$tag", component: ReleaseView });
const runsRoute = createRoute({
  getParentRoute: () => repo,
  path: "/runs",
  // The view ignores a `show` it doesn't know.
  // The view ignores values it doesn't know.
  validateSearch: (s): RunsSearch => ({
    show: str(s.show) as RunsSearch["show"],
    workflow: str(s.workflow),
    view: str(s.view) as RunsSearch["view"],
    start: str(s.start),
  }),
  component: RunsView,
});
const runRoute = createRoute({
  getParentRoute: () => repo,
  path: "/runs/$number",
  validateSearch: (s): RunSearch => ({ job: str(s.job), step: Number(s.step) || undefined, line: Number(s.line) || undefined }),
  component: RunView,
});
export type CodeSearchParams = { q?: string };
const milestones = createRoute({
  getParentRoute: () => repo,
  path: "/issues/milestones",
  validateSearch: (s): { closed?: boolean } => ({ closed: flag(s.closed) }),
  component: MilestonesView,
});
const labels = createRoute({ getParentRoute: () => repo, path: "/issues/labels", component: LabelsView });
// Addresses from the git host a repository came from, with only the host changed, land on the same thing here.
type Moved = { owner: string; repo: string; _splat?: string; number?: string };
const moved = (path: string, to: (p: Moved) => string) =>
  createRoute({
    getParentRoute: () => repo,
    path,
    beforeLoad: ({ params }) => {
      const p = params as Moved;
      throw redirect({ href: `/${p.owner}/${p.repo}/${to(p)}`, replace: true });
    },
  });
const oldLinks = [
  moved("/pull/$number", (p) => `reviews/${p.number}`),
  moved("/pull/$number/$", (p) => `reviews/${p.number}`),
  moved("/pulls", () => "reviews"),
  moved("/commits/$", () => "changes"),
  moved("/commit/$", (p) => `changes?c=${encodeURIComponent(p._splat ?? "")}`),
  moved("/actions/$", () => "runs"),
  ...["/tree/$", "/blob/$"].map((path) =>
    moved(path, (p) => {
      const [ref = "", ...file] = (p._splat ?? "").split("/");
      return `code/${file.join("/")}?ref=${encodeURIComponent(ref)}`;
    }),
  ),
];
const forks = createRoute({ getParentRoute: () => repo, path: "/forks", component: ForksView });
const search = createRoute({ getParentRoute: () => repo, path: "/search", validateSearch: (s): CodeSearchParams => ({ q: str(s.q) }), component: SearchView });
const locks = createRoute({ getParentRoute: () => repo, path: "/locks", component: LocksView });
const agents = createRoute({ getParentRoute: () => repo, path: "/agents", component: AgentsView });
const settings = createRoute({ getParentRoute: () => repo, path: "/settings", component: RepoSettingsView });

// Plain paths (/acme-labs/web/issues/4): links that a server can answer, preview and index. Every
// way the app is served returns index.html for a path that isn't a file (see grovedockd's console
// layer and the Caddyfile), and old `#/` links are moved to their path on load (connection.tsx).
export const router = createRouter({
  routeTree: root.addChildren([
    newRepo,
    importRoute,
    connectRoute,
    importProgress,
    home,
    inbox,
    reviewsQueue,
    statusRoute,
    explore,
    welcome,
    tokens,
    notifications,
    runnersRoute,
    org,
    repo.addChildren([
      overview,
      changes,
      code,
      reviews,
      review,
      issues,
      issue,
      ref,
      releases,
      release,
      runsRoute,
      runRoute,
      search,
      forks,
      milestones,
      labels,
      locks,
      agents,
      settings,
      ...oldLinks,
    ]),
  ]),
  history: createBrowserHistory(),
  // Every search value is a plain string or a flag, so write them plainly (?L=3, not ?L="3");
  // each route's validateSearch turns them back into what it expects.
  parseSearch: parseSearchWith((v) => v),
  stringifySearch: stringifySearchWith((v) => String(v)),
  defaultPreload: "intent",
  scrollRestoration: true,
  // A failing view shows what went wrong in place; navigation keeps working.
  defaultErrorComponent: ({ error, reset }) =>
    (error as Error).name === "ConnectError" ? <ErrorState error={error} retry={reset} title="This page didn't load" /> : <Crashed error={error} />,
  defaultNotFoundComponent: () => <NotFound what="page" />,
});

/** A bug in this page, not a server answer: say so plainly, keep the details for a report. */
function Crashed({ error }: { error: unknown }) {
  return (
    <div className="state" role="alert">
      <h2>This page broke</h2>
      <p>A bug in Grovedock stopped it showing. Reloading usually gets past it; the rest of Grovedock still works.</p>
      <button type="button" className="button" data-primary="" onClick={() => location.reload()}>
        Reload
      </button>
      <details className="state-details">
        <summary>Details for a bug report</summary>
        <code>{error instanceof Error ? error.message : String(error)}</code>
      </details>
    </div>
  );
}

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
